import type { HealthAppReport } from "./api";

const MIB = 1024 * 1024;

/**
 * Capacity columns in the per-app table all render in MiB with two decimals so
 * the numbers line up vertically and the unit can live in the column header
 * instead of repeating per row. `null` (no running container) shows as a dash.
 */
export function formatMiB(bytes: number | null): string {
  if (bytes === null) return "—";
  return (bytes / MIB).toFixed(2);
}

/** Columns the per-app table can be sorted by. */
export type HealthSortKey =
  | "name"
  | "cpuPercent"
  | "memUsageBytes"
  | "volumesBytes"
  | "writableBytes"
  | "totalBytes";

export type SortDirection = "asc" | "desc";

function compareNullableNumber(a: number | null, b: number | null, dir: SortDirection): number {
  // Missing values (null) always sink to the bottom, regardless of direction.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir === "asc" ? a - b : b - a;
}

/**
 * Returns a new, sorted copy of the app rows. The `name` column sorts
 * case-insensitively; all numeric columns push `null` (no running container)
 * to the end in both directions.
 */
export function sortHealthApps(
  apps: readonly HealthAppReport[],
  key: HealthSortKey,
  dir: SortDirection,
): HealthAppReport[] {
  const copy = [...apps];
  copy.sort((a, b) => {
    if (key === "name") {
      const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      return dir === "asc" ? cmp : -cmp;
    }
    return compareNullableNumber(a[key], b[key], dir);
  });
  return copy;
}
